import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Spade, Trophy, Loader2, TrendingUp, TrendingDown, Camera, Trash2, Crown } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import UserAvatar from "../components/UserAvatar";

export default function Profile() {
    const { username } = useParams();
    const { user, refresh } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadErr, setUploadErr] = useState("");
    const [avatarVer, setAvatarVer] = useState("");
    const fileRef = useRef(null);

    const isOwner = user && user !== false && user.username?.toLowerCase() === (username || "").toLowerCase();

    useEffect(() => {
        setLoading(true);
        setError("");
        api.get(`/users/${encodeURIComponent(username)}/profile`)
            .then(({ data }) => setProfile(data))
            .catch((e) => setError(e?.response?.data?.detail || "Could not load profile"))
            .finally(() => setLoading(false));
    }, [username]);

    const pick = () => fileRef.current?.click();

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return setUploadErr("Only image files");
        if (f.size > 5 * 1024 * 1024) return setUploadErr("Image exceeds 5MB");
        setUploadErr("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setAvatarVer(String(Date.now()));
            setProfile((p) => (p ? { ...p, has_avatar: true } : p));
            refresh && refresh();
        } catch (err) {
            setUploadErr(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
        }
    };

    const removeAvatar = async () => {
        if (!window.confirm("Remove your profile picture?")) return;
        setUploading(true);
        try {
            await api.delete("/users/me/avatar");
            setAvatarVer(String(Date.now()));
            setProfile((p) => (p ? { ...p, has_avatar: false } : p));
            refresh && refresh();
        } catch {}
        finally { setUploading(false); }
    };

    return (
        <div className="min-h-screen bg-cream">
            <div className="max-w-3xl mx-auto px-6 py-10">
                {loading ? (
                    <div className="text-center py-20">
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-vibrantorange" strokeWidth={2.5} />
                    </div>
                ) : error ? (
                    <div className="brutal-card p-10 bg-vibrantpink/10 text-center" data-testid="profile-error">
                        <p className="font-heading font-black text-xl text-ink">{error}</p>
                        <Link to="/" className="brutal-btn-white mt-4 inline-flex">← Home</Link>
                    </div>
                ) : profile && (
                    <>
                        <div className="mb-8 flex items-center gap-4">
                            <div className="relative">
                                <UserAvatar
                                    username={profile.username}
                                    size={96}
                                    version={avatarVer || (profile.has_avatar ? "1" : "0")}
                                    className="rounded-2xl !border-[4px] !shadow-brutal-lg"
                                    textClass="text-3xl"
                                />
                                {isOwner && (
                                    <button
                                        type="button"
                                        onClick={pick}
                                        disabled={uploading}
                                        data-testid="profile-avatar-upload-btn"
                                        title="Change profile picture"
                                        className="absolute -bottom-1 -right-1 w-9 h-9 bg-vibrantorange text-white border-[3px] border-ink rounded-full flex items-center justify-center shadow-brutal hover:-translate-y-0.5 transition-all disabled:opacity-60"
                                    >
                                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <Camera className="w-4 h-4" strokeWidth={2.5} />}
                                    </button>
                                )}
                            </div>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-avatar-file-input" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h1 className="font-heading font-black text-4xl text-ink tracking-tighter break-words" data-testid="profile-username">
                                        @{profile.username}
                                    </h1>
                                    {profile.champion_rank && (
                                        <Link
                                            to="/poker/leaderboard"
                                            data-testid="profile-champion-badge"
                                            title={`Ranked #${profile.champion_rank} this month`}
                                            className="inline-flex items-center gap-1 bg-vibrantyellow border-[3px] border-ink rounded-xl px-2 py-1 shadow-brutal hover:-translate-y-0.5 transition-all"
                                        >
                                            <Crown className="w-4 h-4 text-vibrantorange" strokeWidth={2.5} fill="currentColor" />
                                            <span className="font-heading font-black text-xs text-ink">Champion #{profile.champion_rank}</span>
                                        </Link>
                                    )}
                                </div>
                                {profile.member_since && (
                                    <p className="font-body text-sm text-ink/60">
                                        member since {new Date(profile.member_since).toLocaleDateString()}
                                    </p>
                                )}
                                {isOwner && profile.has_avatar && (
                                    <button
                                        type="button"
                                        onClick={removeAvatar}
                                        disabled={uploading}
                                        data-testid="profile-avatar-remove-btn"
                                        className="mt-2 inline-flex items-center gap-1 font-heading font-bold text-xs uppercase tracking-widest text-ink/50 hover:text-vibrantpink"
                                    >
                                        <Trash2 className="w-3 h-3" strokeWidth={2.5} /> Remove picture
                                    </button>
                                )}
                            </div>
                        </div>

                        {uploadErr && (
                            <div data-testid="profile-avatar-error" className="brutal-card p-3 bg-vibrantpink/15 border-vibrantpink mb-4">
                                <p className="font-body font-semibold text-sm text-ink">{uploadErr}</p>
                            </div>
                        )}

                        {/* Poker card */}
                        <div className="brutal-card p-6 bg-white" data-testid="profile-poker-stats">
                            <div className="flex items-center gap-2 mb-4">
                                <Spade className="w-5 h-5 text-ink" strokeWidth={2.5} />
                                <h2 className="font-heading font-black text-2xl text-ink tracking-tight">Poker record</h2>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="brutal-card p-3 bg-cream">
                                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Played</div>
                                    <div className="font-heading font-black text-3xl text-ink tabular-nums" data-testid="profile-poker-played">
                                        {profile.poker.played}
                                    </div>
                                </div>
                                <div className="brutal-card p-3 bg-emerald-600 text-white">
                                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-white/80">Wins</div>
                                    <div className="font-heading font-black text-3xl tabular-nums" data-testid="profile-poker-wins">
                                        {profile.poker.wins}
                                    </div>
                                </div>
                                <div className="brutal-card p-3 bg-vibrantpink text-white">
                                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-white/80">Losses</div>
                                    <div className="font-heading font-black text-3xl tabular-nums" data-testid="profile-poker-losses">
                                        {profile.poker.losses}
                                    </div>
                                </div>
                                <div className={`brutal-card p-3 ${profile.poker.net_pnl >= 0 ? "bg-vibrantyellow" : "bg-ink text-white"}`}>
                                    <div className={`font-heading font-bold text-[10px] uppercase tracking-widest ${profile.poker.net_pnl >= 0 ? "text-ink/60" : "text-white/70"}`}>Net P&L</div>
                                    <div className="font-heading font-black text-3xl tabular-nums flex items-center gap-1" data-testid="profile-poker-pnl">
                                        {profile.poker.net_pnl >= 0 ? <TrendingUp className="w-5 h-5" strokeWidth={3} /> : <TrendingDown className="w-5 h-5" strokeWidth={3} />}
                                        {profile.poker.net_pnl > 0 ? "+" : ""}{profile.poker.net_pnl}
                                    </div>
                                </div>
                            </div>
                            {profile.poker.played === 0 && (
                                <p className="font-body text-sm text-ink/50 mt-4">
                                    <Trophy className="w-3.5 h-3.5 inline mr-1" /> No matches yet.{" "}
                                    <Link to="/poker" className="underline font-heading font-bold text-vibrantorange">Play a hand</Link>
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
